import { EventEmitter } from 'events'

const isInvalidEventName = (eventName) => !eventName || typeof eventName !== 'string'

const ev = new EventEmitter()

/**
 * @constant
 * @type {Object}
 * @description NameSpace constants
 */
const nameSpaces = Object.freeze({
  REPEAT: 'repeat',
})

/**
 * @constant
 * @type {Object}
 * @description Event constants
 */
const events = Object.freeze({
  DELETE: 'delete',
})

/**
 * @constant
 * @type {Object}
 * @description Validation messages
 */
const validationMessages = {
  INVALID_NAMESPACE: 'Invalid nameSpace',
  INVALID_EVENT: 'Invalid event',
}

/**
 * Validates if the provided nameSpace and eventName are valid.
 *
 * @param {string} nameSpace - The nameSpace value to validate.
 * @param {string} eventName - The eventName value to validate.
 * @returns {boolean} - Returns true if both are valid, otherwise false.
 */
function isValidEvent(nameSpace, eventName) {
  const isNamespaceValid = Object.values(nameSpaces).includes(nameSpace)
  if (!isNamespaceValid) {
    console.log(validationMessages.INVALID_NAMESPACE)
    return false
  }

  const isEventValid = Object.values(events).includes(eventName)
  if (!isEventValid) {
    console.log(validationMessages.INVALID_EVENT)
    return false
  }
  return true
}

/**
 * Generates an event name string based on the provided nameSpace and eventName.
 *
 * @param {string} nameSpace - The nameSpace value.
 * @param {string} eventName - The eventName value.
 * @returns {string|null} - Returns the generated event name, or null if invalid.
 */
const generateEventName = (nameSpace, eventName) => {
  if (!isValidEvent(nameSpace, eventName)) {
    return null
  }

  return `${nameSpace}:${eventName}`
}

/**
 * Subscribes to an event with a callback function.
 *
 * @param {string} eventName - The name of the event to subscribe to, generated by generateEventName.
 * @param {Function} cb - The callback function to execute when the event is emitted.
 * @returns {EventEmitter|null} - Returns the EventEmitter instance or null if invalid.
 */
const subscribe = (eventName, cb) => {
  if (ev.listenerCount(eventName) > 1 || isInvalidEventName(eventName)) {
    return null
  }

  return ev.on(eventName, (data) => cb(data))
}

/**
 * Publishes (emits) an event.
 *
 * @param {string} eventName - The name of the event to publish, generated by generateEventName.
 * @param {any} [data] - Optional data to pass to the event listeners.
 * @returns {boolean|null} - Returns true if the event had listeners, false otherwise, or null if invalid.
 */
const publish = (eventName, data) => {
  if (isInvalidEventName(eventName)) {
    return null
  }

  return ev.emit(eventName, data)
}

/**
 * Unsubscribes from an event, removing the specified callback function.
 *
 * @param {string} eventName - The name of the event to unsubscribe from, generated by generateEventName.
 * @param {Function} cb - The callback function to remove.
 * @returns {EventEmitter|null} - Returns the EventEmitter instance or null if invalid.
 */
const unsubscribe = (eventName, cb) => {
  if (isInvalidEventName(eventName)) {
    return null
  }

  return ev.removeListener(eventName, cb)
}

export { generateEventName, subscribe, publish, unsubscribe, events, nameSpaces }
